import { useEffect, useState } from "react";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, Plus, ChevronRight, Send, FileText, Layers } from "lucide-react";
import { toast } from "sonner";

export default function Credits() {
  const { user } = useAuth();
  const [summary, setSummary] = useState([]);
  const [activeCust, setActiveCust] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [statement, setStatement] = useState(null);
  const [view, setView] = useState("ledger"); // 'ledger' or 'statement'
  const [payOpen, setPayOpen] = useState(false);
  const [activeCredit, setActiveCredit] = useState(null);
  const [payForm, setPayForm] = useState({ amount_paid: "", payment_mode: "Cash", notes: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ amount_paid: "", payment_mode: "Cash", notes: "" });

  const loadSummary = async () => {
    const { data } = await api.get("/credits/outstanding/summary");
    setSummary(data);
  };
  useEffect(() => { loadSummary(); }, []);

  const openCustomer = async (customer_id) => {
    setActiveCust(customer_id);
    setView("ledger");
    const [{ data: l }, { data: s }] = await Promise.all([
      api.get(`/credits/customer/${customer_id}`),
      api.get(`/ledger/customer/${customer_id}`),
    ]);
    setLedger(l);
    setStatement(s);
  };

  const shareStatement = async () => {
    try {
      const { data } = await api.post(`/ledger/share/whatsapp/customer/${activeCust}`);
      toast.success(`Statement sent via ${data.sent_via}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startPayment = (credit) => {
    setActiveCredit(credit);
    setPayForm({ amount_paid: String(credit.balance), payment_mode: "Cash", notes: "" });
    setPayOpen(true);
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post("/credits/payments", {
        customer_credit_id: activeCredit.id,
        amount_paid: parseFloat(payForm.amount_paid),
        payment_mode: payForm.payment_mode,
        notes: payForm.notes,
      });
      toast.success("Payment recorded");
      setPayOpen(false);
      await openCustomer(activeCust);
      loadSummary();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitBulkPayment = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/credits/bulk-payment", {
        customer_id: activeCust,
        amount_paid: parseFloat(bulkForm.amount_paid),
        payment_mode: bulkForm.payment_mode,
        notes: bulkForm.notes,
      });
      toast.success(`Applied ${formatINR(data.total_applied)} across ${data.payments.length} bill(s)`);
      setBulkOpen(false);
      setBulkForm({ amount_paid: "", payment_mode: "Cash", notes: "" });
      await openCustomer(activeCust);
      loadSummary();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (activeCust && ledger) {
    return (
      <div className="space-y-4" data-testid="credit-ledger-detail">
        <div className="flex justify-between items-center">
          <Button variant="ghost" onClick={() => { setActiveCust(null); setLedger(null); setStatement(null); }} className="rounded-none" data-testid="back-to-summary">← Back to all customers</Button>
          <div className="flex gap-2">
            <div className="inline-flex border border-slate-200 rounded-sm">
              <button onClick={() => setView("ledger")} className={`px-3 py-1.5 text-xs uppercase tracking-widest ${view === "ledger" ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="view-ledger-tab">Credit Ledger</button>
              <button onClick={() => setView("statement")} className={`px-3 py-1.5 text-xs uppercase tracking-widest ${view === "statement" ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="view-statement-tab">Full Statement</button>
            </div>
            <Button variant="outline" onClick={shareStatement} className="rounded-none" data-testid="share-cust-statement"><Send className="w-4 h-4 mr-1" />WhatsApp</Button>
            <Button variant="outline" onClick={() => window.print()} className="rounded-none" data-testid="print-cust-statement"><FileText className="w-4 h-4 mr-1" />Print</Button>
            {ledger.outstanding_total > 0 && (
              <Button onClick={() => { setBulkForm({ amount_paid: String(ledger.outstanding_total), payment_mode: "Cash", notes: "" }); setBulkOpen(true); }}
                className="bg-emerald-600 hover:bg-emerald-700 rounded-none" data-testid="bulk-pay-btn">
                <Layers className="w-4 h-4 mr-1" />Record Bulk Payment
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <Kpi label="Customer" value={ledger.customer.name} />
          <Kpi label="Outstanding" value={formatINR(ledger.outstanding_total)} accent />
          <Kpi label="Credit Limit" value={formatINR(ledger.credit_limit)} />
          <Kpi label="Store Credit" value={formatINR(ledger.store_credit)} />
        </div>

        {view === "statement" && statement && (
          <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto" data-testid="customer-statement-table">
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs uppercase tracking-[0.2em] text-slate-500">Full Statement · {statement.entries.length} entries</div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <th className="px-4 py-3 text-left font-semibold">Date</th>
                <th className="px-4 py-3 text-left font-semibold">Type</th>
                <th className="px-4 py-3 text-left font-semibold">Ref</th>
                <th className="px-4 py-3 text-left font-semibold">Description</th>
                <th className="px-4 py-3 text-right font-semibold">Debit</th>
                <th className="px-4 py-3 text-right font-semibold">Credit</th>
                <th className="px-4 py-3 text-right font-semibold">Balance</th>
              </tr></thead>
              <tbody>
                {statement.entries.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No entries.</td></tr>}
                {statement.entries.map((e, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${e.type === "credit_payment" ? "bg-green-50/40" : e.type === "return" ? "bg-orange-50/40" : ""}`} data-testid={`stmt-row-${i}`}>
                    <td className="px-4 py-3 text-xs">{e.date}</td>
                    <td className="px-4 py-3 text-xs uppercase tracking-widest text-slate-500">{(e.type || "").replace("_", " ")}</td>
                    <td className="px-4 py-3 font-mono text-xs">{e.ref}</td>
                    <td className="px-4 py-3 text-slate-700">{e.description}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{e.debit !== 0 ? formatINR(e.debit) : ""}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-green-700">{e.credit > 0 ? formatINR(e.credit) : ""}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold">{formatINR(e.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {view === "ledger" && (<>
        <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto">
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs uppercase tracking-[0.2em] text-slate-500">Credit entries</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="px-4 py-3 text-left font-semibold">Bill #</th>
              <th className="px-4 py-3 text-left font-semibold">Date</th>
              <th className="px-4 py-3 text-right font-semibold">Total Bill</th>
              <th className="px-4 py-3 text-right font-semibold">Paid Upfront</th>
              <th className="px-4 py-3 text-right font-semibold">Credit</th>
              <th className="px-4 py-3 text-right font-semibold">Paid Later</th>
              <th className="px-4 py-3 text-right font-semibold">Balance</th>
              <th className="px-4 py-3 text-right font-semibold">Action</th>
            </tr></thead>
            <tbody>
              {ledger.credits.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No credit entries.</td></tr>}
              {ledger.credits.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`credit-row-${c.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{c.bill_number}</td>
                  <td className="px-4 py-3 text-xs">{(c.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatINR(c.total_bill_amount)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatINR(c.amount_paid_upfront)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatINR(c.credit_amount)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatINR(c.amount_paid_later)}</td>
                  <td className="px-4 py-3 text-right font-bold tabular-nums">{formatINR(c.balance)}</td>
                  <td className="px-4 py-3 text-right">
                    {c.balance > 0 && <Button size="sm" className="bg-slate-900 hover:bg-slate-800 rounded-none h-7" onClick={() => startPayment(c)} data-testid={`record-pay-${c.id}`}><Plus className="w-3 h-3 mr-1" />Record</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto">
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs uppercase tracking-[0.2em] text-slate-500">Payment History</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="px-4 py-3 text-left font-semibold">Date</th>
              <th className="px-4 py-3 text-left font-semibold">Mode</th>
              <th className="px-4 py-3 text-right font-semibold">Amount</th>
              <th className="px-4 py-3 text-left font-semibold">Received By</th>
              <th className="px-4 py-3 text-left font-semibold">Notes</th>
            </tr></thead>
            <tbody>
              {ledger.payments.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No payments yet.</td></tr>}
              {ledger.payments.map((p) => (
                <tr key={p.id} className="border-b border-slate-100">
                  <td className="px-4 py-3">{p.payment_date}</td>
                  <td className="px-4 py-3">{p.payment_mode}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{formatINR(p.amount_paid)}</td>
                  <td className="px-4 py-3 text-slate-600">{p.received_by_name}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{p.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>)}

        <Dialog open={payOpen} onOpenChange={setPayOpen}>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader><DialogTitle>Record Payment · {activeCredit?.bill_number}</DialogTitle></DialogHeader>
            {activeCredit && (
              <form onSubmit={submitPayment} className="space-y-3">
                <div className="text-xs text-slate-500">Outstanding: <span className="font-bold text-slate-900">{formatINR(activeCredit.balance)}</span></div>
                <div><Label>Amount</Label>
                  <Input type="number" step="0.01" min="0.01" max={activeCredit.balance} value={payForm.amount_paid} onChange={(e) => setPayForm({ ...payForm, amount_paid: e.target.value })} required className="rounded-none mt-1" data-testid="pay-amount" />
                </div>
                <div><Label>Mode</Label>
                  <Select value={payForm.payment_mode} onValueChange={(v) => setPayForm({ ...payForm, payment_mode: v })}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="pay-mode"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Cash">Cash</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="Card">Card</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Notes</Label>
                  <Input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} className="rounded-none mt-1" data-testid="pay-notes" />
                </div>
                <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="submit-pay">Record Payment</Button></DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader><DialogTitle>Bulk Payment · {ledger?.customer?.name}</DialogTitle></DialogHeader>
            <form onSubmit={submitBulkPayment} className="space-y-3">
              <div className="text-xs text-slate-600 bg-emerald-50 border border-emerald-200 p-2">
                The amount will be applied <b>FIFO</b> (oldest credits first) until it's exhausted or all credits are cleared.
              </div>
              <div className="text-xs text-slate-500">Total Outstanding: <span className="font-bold text-slate-900">{formatINR(ledger?.outstanding_total || 0)}</span></div>
              <div><Label>Amount</Label>
                <Input type="number" step="0.01" min="0.01" max={ledger?.outstanding_total || 0}
                  value={bulkForm.amount_paid}
                  onChange={(e) => setBulkForm({ ...bulkForm, amount_paid: e.target.value })}
                  required className="rounded-none mt-1" data-testid="bulk-pay-amount" />
              </div>
              <div><Label>Mode</Label>
                <Select value={bulkForm.payment_mode} onValueChange={(v) => setBulkForm({ ...bulkForm, payment_mode: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="bulk-pay-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Notes (optional)</Label>
                <Input value={bulkForm.notes} onChange={(e) => setBulkForm({ ...bulkForm, notes: e.target.value })} className="rounded-none mt-1" data-testid="bulk-pay-notes" />
              </div>
              <DialogFooter><Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 rounded-none" data-testid="submit-bulk-pay">Apply Payment</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="credits-summary">
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Customers with Outstanding" value={summary.length} />
        <Kpi label="Total Outstanding" value={formatINR(summary.reduce((s, r) => s + r.outstanding, 0))} accent />
        <Kpi label="Total Bills" value={summary.reduce((s, r) => s + (r.bill_count || 0), 0)} />
      </div>
      <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto">
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs uppercase tracking-[0.2em] text-slate-500">Customers with Outstanding Credit</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            <th className="px-4 py-3 text-left font-semibold">Customer</th>
            <th className="px-4 py-3 text-left font-semibold">Phone</th>
            <th className="px-4 py-3 text-right font-semibold">Total Credit</th>
            <th className="px-4 py-3 text-right font-semibold">Upfront Paid</th>
            <th className="px-4 py-3 text-right font-semibold">Paid Later</th>
            <th className="px-4 py-3 text-right font-semibold">Outstanding</th>
            <th className="px-4 py-3 text-right font-semibold">Limit</th>
            <th className="px-4 py-3 text-center"></th>
          </tr></thead>
          <tbody>
            {summary.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No outstanding credits.</td></tr>}
            {summary.map((r) => (
              <tr key={r.customer_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openCustomer(r.customer_id)} data-testid={`summary-row-${r.customer_id}`}>
                <td className="px-4 py-3 font-medium">{r.customer_name}</td>
                <td className="px-4 py-3 text-slate-600">{r.customer_phone}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatINR(r.total_credit)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatINR(r.amount_paid_upfront)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatINR(r.amount_paid_later)}</td>
                <td className="px-4 py-3 text-right font-bold tabular-nums text-orange-600">{formatINR(r.outstanding)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-500">{formatINR(r.credit_limit)}</td>
                <td className="px-4 py-3 text-center"><ChevronRight className="w-4 h-4 text-slate-400 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`p-4 border ${accent ? "border-orange-300 bg-orange-50" : "border-slate-200 bg-white"} rounded-sm`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
    </div>
  );
}
